import {
	firestore as db, functions
} from '@jaymathew/ui-shared/apis/FirebaseUtilities/FirebaseConfig'
import { httpsCallable, HttpsCallableResult } from 'firebase/functions'
import { getSelectedCompanyId } from 'shared/functions/selectedCompanyAccess'
import {
	collection,
	DocumentData,
	getDocs,
	limit,
	orderBy,
	query,
	QueryDocumentSnapshot,
	startAfter,
	where
} from 'firebase/firestore'
import { encodeAndTokenizeForQueries } from '@jaymathew/utils/Search'
import { getCurrentUser } from 'shared/functions/currentUserAccess'
import { NotificationInput, Role, Notification } from 'resources/types'


// ============ Input Types ============

export interface CreateNotificationsInput {
	title: string
	body: string
	deliveryMethods: ('emailOrSms' | 'inApp')[]
	recipientUIDs?: string[]
	roles?: Role[]
	sendAt?: Date
	persistent?: boolean
	expiresAt?: Date
	action?: {
		label: string
		url: string
	}
}

export interface EditUserNotificationInput {
	notificationID: string
	dismissed?: boolean
	read?: boolean
	deleted?: boolean
}

export interface CreateAutoNotificationInput {
	name: string
	title: string
	targetDaysWithoutPost: number
	message: string
	sendTo: {
		primaryHomeBuyer: boolean
		staffUIDs: string[]
	}
}

export interface DeleteAutoNotificationInput {
	autoNotificationID: string
}

export interface EditAutoNotificationInput {
	autoNotificationID: string
	name: string
	title: string
	targetDaysWithoutPost: number
	message: string
	sendTo: {
		primaryHomeBuyer: boolean
		staffUIDs: string[]
	}
	enabled: boolean
}

// ============ Network Functions ============

export interface GetNotificationsReturnType<T> {
	payload: T[],
	lastDocument?: QueryDocumentSnapshot<DocumentData>
}

export const getUsersNotifications = async (sortBy: keyof Notification, sort: 'asc' | 'desc' = 'desc', limitNumber?: number, searchTerm?: string, lastDocument?: QueryDocumentSnapshot<DocumentData>): Promise<{ payload: Notification[], lastDocument?: QueryDocumentSnapshot<DocumentData> }> => {
	const currentUser = getCurrentUser()
	const companyID = getSelectedCompanyId() ?? ''
	const collectionRef = collection(db, `companies/${companyID}/users/${currentUser?.uid}/notifications`)
	let notificationsQuery = query(collectionRef, orderBy(sortBy.toString(), sort))

	if (searchTerm) {
		const encodedSearchTerm = await encodeAndTokenizeForQueries(searchTerm)
		notificationsQuery = query(notificationsQuery, where('searchTerms', 'array-contains-any', encodedSearchTerm))
	}

	if (lastDocument) {
		notificationsQuery = query(notificationsQuery, startAfter(lastDocument))
	}

	if (limitNumber !== undefined) {
		notificationsQuery = query(notificationsQuery, limit(limitNumber))
	}

	const notificatonsCollection = await getDocs(notificationsQuery)

	if (notificatonsCollection.empty) {
		return {
			payload: [],
			lastDocument: undefined,
		}
	}

	const notificatons: Notification[] = notificatonsCollection.docs.map((doc) => doc.data() as Notification)

	return {
		payload: notificatons,
		lastDocument: notificatonsCollection.docs[notificatonsCollection.docs.length - 1],
	}
}

export const getCompanyNotifications = async (sortBy: keyof NotificationInput, sort: 'asc' | 'desc' = 'desc', limitNumber?: number, searchTerm?: string, lastDocument?: QueryDocumentSnapshot<DocumentData>): Promise<{ payload: NotificationInput[], lastDocument?: QueryDocumentSnapshot<DocumentData> }> => {
	const companyID = getSelectedCompanyId() ?? ''
	const collectionRef = collection(db, `companies/${companyID}/campaigns`)
	let notificationsQuery = query(collectionRef, orderBy(sortBy.toString(), sort))

	if (searchTerm) {
		const encodedSearchTerm = await encodeAndTokenizeForQueries(searchTerm)
		notificationsQuery = query(notificationsQuery, where('searchTerms', 'array-contains-any', encodedSearchTerm))
	}

	if (lastDocument) {
		notificationsQuery = query(notificationsQuery, startAfter(lastDocument))
	}

	if (limitNumber !== undefined) {
		notificationsQuery = query(notificationsQuery, limit(limitNumber))
	}


	const notificatonsCollection = await getDocs(notificationsQuery)

	if (notificatonsCollection.empty) {
		return {
			payload: [],
			lastDocument: undefined,
		}
	}

	const notificatons: NotificationInput[] = notificatonsCollection.docs.map((doc) => doc.data() as NotificationInput)

	return {
		payload: notificatons,
		lastDocument: notificatonsCollection.docs[notificatonsCollection.docs.length - 1],
	}

}

export const createNotifications = async (data: CreateNotificationsInput): Promise<HttpsCallableResult> => {
	const companyID = getSelectedCompanyId() ?? ''
	const callable = httpsCallable(functions, 'firebasefuncCreateNotifications')
	const result: HttpsCallableResult = await callable({ companyID, ...data })
	return result
}

export const editUserNotification = async (data: EditUserNotificationInput): Promise<HttpsCallableResult> => {
	const companyID = getSelectedCompanyId() ?? ''
	const callable = httpsCallable(functions, 'firebasefuncEditUserNotification')
	const result: HttpsCallableResult = await callable({ companyID, ...data })
	return result
}
